import {
  CreditPlanDatasetCsvRequestModel,
  CreditPlanLibrarySaveRequest,
  CreditPlanRequestModel,
  CreditPlanResponseModel,
  CreditPlanResultItem
} from '@/@types/client-api';
import {
  mapThumbFeedbackUIValueToThumbValue,
  mapCreditPlanFailureReason,
  mapCreditPlanStatus
} from '@/components/ui/CreditPlan/util/CreditPlanHelpers';
import type { ThumbFeedbackValue } from '@/components/ui/Feedback/ThumbFeedbackButtons';
import { ClientAPI } from '@/redux/api';
import { processDownloadedFile } from '@/redux/helpers/fileManagement';

type FetchPregeneratedCreditPlanRequest = {
  planId: string;
  licenseIdentifier: string;
  noAi?: boolean;
  temporalIdentifier?: string;
};

type RefreshCreditPlanResultItemsRequest = FetchPregeneratedCreditPlanRequest & {
  itemPks: string[];
};

const latestRefreshRequestByPlanItem = new Map<string, string>();
const getPlanItemRefreshKey = (request: FetchPregeneratedCreditPlanRequest, itemPk: string) =>
  `${request.licenseIdentifier}:${request.planId}:${request.noAi ?? false}:${request.temporalIdentifier ?? ''}:${itemPk}`;

type ModifyLibraryRequest = CreditPlanLibrarySaveRequest & {
  license: string;
};

type SubmitCreditPlanFeedbackRequest = {
  planId: string;
  thumbValue: ThumbFeedbackValue;
};

export type ResendCreditPlanEmailRequest = {
  processId: string;
  licenseIdentifier?: string;
};

export type DownloadCreditPlanDatasetCsvRequest = CreditPlanDatasetCsvRequestModel & {
  jurisdiction: string;
};

export type CreateCreditPlanRequest = CreditPlanRequestModel & {
  indivSk?: string;
};

const pregeneratedCreditPlanEndpoints = ClientAPI.injectEndpoints({
  endpoints: builder => ({
    getPregeneratedCreditPlan: builder.query<
      CreditPlanResponseModel,
      FetchPregeneratedCreditPlanRequest
    >({
      query: req => {
        const params = new URLSearchParams();
        if (req.temporalIdentifier) params.set('temporalNo', req.temporalIdentifier);

        const queryString = params.toString();
        return {
          url: `credit-plans/pregenerated/${req.licenseIdentifier}/${req.planId}${queryString ? `?${queryString}` : ''}`,
          method: 'GET'
        };
      },
      providesTags: result => [
        {
          type: 'CreditPlans' as const,
          id: result?.processId ?? result?.creditPlan?.id ?? undefined
        }
      ],
      transformResponse(res: CreditPlanResponseModel) {
        return {
          ...res,
          status: mapCreditPlanStatus(res.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(res.failureReason as unknown as number)
        };
      }
    })
  })
});

const creditPlanEndpoints = pregeneratedCreditPlanEndpoints.injectEndpoints({
  endpoints: builder => ({
    getCreditPlan: builder.query<CreditPlanResponseModel, string>({
      query: planId => ({
        url: `credit-plans/${planId}`,
        method: 'GET'
      }),
      providesTags: result => [
        {
          type: 'CreditPlans' as const,
          id: result?.processId ?? result?.creditPlan?.id ?? undefined
        }
      ],
      transformResponse(res: CreditPlanResponseModel) {
        return {
          ...res,
          status: mapCreditPlanStatus(res.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(res.failureReason as unknown as number)
        };
      }
    }),
    refreshGeneratedCreditPlanResultItems: builder.mutation<
      Record<string, CreditPlanResultItem[]>,
      RefreshCreditPlanResultItemsRequest
    >({
      query: ({ itemPks }) => {
        const params = new URLSearchParams();
        itemPks.forEach(pk => params.append('itemPks', pk));

        return {
          url: `credit-plans/refresh-plan-items?${params}`,
          method: 'GET'
        };
      },
      async onQueryStarted({ itemPks, ...planRequest }, { dispatch, queryFulfilled, requestId }) {
        const requestedItemPks = new Set(itemPks);
        const refreshKeys = [...requestedItemPks].map(itemPk =>
          getPlanItemRefreshKey(planRequest, itemPk)
        );
        refreshKeys.forEach(key => latestRefreshRequestByPlanItem.set(key, requestId));

        try {
          const { data } = await queryFulfilled;
          const refreshedItems = new Map(
            Object.values(data ?? {})
              .flat()
              .filter(
                (item): item is CreditPlanResultItem & { variationPk: string } =>
                  !!item.variationPk &&
                  requestedItemPks.has(item.variationPk) &&
                  latestRefreshRequestByPlanItem.get(
                    getPlanItemRefreshKey(planRequest, item.variationPk)
                  ) === requestId
              )
              .map(item => [item.variationPk, item])
          );
          if (!refreshedItems.size) return;

          dispatch(
            pregeneratedCreditPlanEndpoints.util.updateQueryData(
              'getPregeneratedCreditPlan',
              planRequest,
              draft => {
                draft.creditPlan?.creditProductGroups?.forEach(group => {
                  group.searchResultItems = group.searchResultItems?.map(
                    item => (item.variationPk && refreshedItems.get(item.variationPk)) || item
                  );
                });
              }
            )
          );
        } catch {
          return;
        } finally {
          refreshKeys.forEach(key => {
            if (latestRefreshRequestByPlanItem.get(key) === requestId)
              latestRefreshRequestByPlanItem.delete(key);
          });
        }
      }
    }),
    getAllCreditPlans: builder.query<CreditPlanResponseModel[], void>({
      query: () => ({
        url: `credit-plans/all`,
        method: 'GET'
      }),
      providesTags: result =>
        result?.length
          ? [
              ...result.map(plan => ({
                type: 'CreditPlans' as const,
                id: plan?.processId ?? plan?.creditPlan?.id ?? undefined
              })),
              { type: 'CreditPlans' as const, id: 'LIST' }
            ]
          : [{ type: 'CreditPlans' as const, id: 'LIST' }],
      transformResponse(res: CreditPlanResponseModel[]) {
        return res.map(plan => ({
          ...plan,
          status: mapCreditPlanStatus(plan.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(plan.failureReason as unknown as number)
        }));
      }
    }),
    getExcludedLicenses: builder.query<string[], void>({
      query: () => ({
        url: `credit-plans/excluded-licenses`,
        method: 'GET'
      })
    }),
    deleteCreditPlan: builder.query<boolean, string>({
      query: planId => ({
        url: `credit-plans/${planId}`,
        method: 'DELETE'
      })
    }),
    saveAllToLibrary: builder.mutation<Record<string, boolean>, ModifyLibraryRequest>({
      query: req => ({
        url: `credit-plans/save-items/${req.license}`,
        body: req,
        method: 'POST'
      }),
      invalidatesTags: ['Library', 'LibraryItem']
    }),
    removeAllFromLibrary: builder.mutation<Record<string, boolean>, ModifyLibraryRequest>({
      query: req => ({
        url: `credit-plans/delete-items/${req.license}`,
        body: [...(req.codes || [])],
        method: 'DELETE'
      })
    }),
    submitCreditPlanFeedback: builder.mutation<void, SubmitCreditPlanFeedbackRequest>({
      query: ({ planId, thumbValue }) => ({
        url: `credit-plans/${planId}/feedback`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mapThumbFeedbackUIValueToThumbValue(thumbValue)),
        responseHandler: async response => {
          if (response.status === 204) return null;
          const text = await response.text();
          return text ? JSON.parse(text) : null;
        },
        validateStatus: response => response.status >= 200 && response.status < 300
      }),
      invalidatesTags: (_result, _error, { planId }) => [
        { type: 'CreditPlans' as const, id: planId },
        { type: 'CreditPlans' as const, id: 'LIST' }
      ]
    }),
    createCreditPlan: builder.mutation<CreditPlanResponseModel, CreateCreditPlanRequest>({
      query: req => ({
        url: 'credit-plans',
        body: req,
        method: 'POST'
      }),
      transformResponse(res: CreditPlanResponseModel) {
        return {
          ...res,
          status: mapCreditPlanStatus(res.status as unknown as number),
          failureReason: mapCreditPlanFailureReason(res.failureReason as unknown as number)
        };
      }
    }),
    downloadCreditPlanDatasetCsv: builder.mutation<void, DownloadCreditPlanDatasetCsvRequest>({
      query: request => ({
        url: 'credit-plans/dataset-csv',
        body: request,
        method: 'POST',
        cache: 'no-cache',
        responseHandler: async response => {
          if (response.status === 200) {
            const blob = await response.blob();
            processDownloadedFile(getDownloadFileName(response, request.jurisdiction), blob);
          }

          return null;
        }
      })
    }),
    resendCreditPlanEmail: builder.mutation<{ status?: string }, ResendCreditPlanEmailRequest>({
      query: req => ({
        url: 'credit-plans/resend-email',
        body: req.processId && !req.licenseIdentifier ? { processId: req.processId } : req,
        method: 'POST',
        validateStatus: response => {
          return response.status === 200 || response.status === 202;
        }
      })
    })
  })
});

export const {
  useGetCreditPlanQuery,
  useLazyGetCreditPlanQuery,
  useGetPregeneratedCreditPlanQuery,
  useLazyGetPregeneratedCreditPlanQuery,
  useRefreshGeneratedCreditPlanResultItemsMutation,
  useGetAllCreditPlansQuery,
  useGetExcludedLicensesQuery,
  useLazyDeleteCreditPlanQuery,
  useSaveAllToLibraryMutation,
  useRemoveAllFromLibraryMutation,
  useCreateCreditPlanMutation,
  useDownloadCreditPlanDatasetCsvMutation,
  useResendCreditPlanEmailMutation,
  useSubmitCreditPlanFeedbackMutation
} = creditPlanEndpoints;

export default creditPlanEndpoints;

const getDownloadFileName = (response: Response, jurisdiction: string) => {
  const contentDisposition = response.headers.get('content-disposition');
  const utf8FileName = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf8FileName) return decodeURIComponent(utf8FileName);

  const basicFileName = contentDisposition?.match(/filename="?([^"]+)"?/i)?.[1];
  if (basicFileName) return basicFileName;

  return `credit-plan-dataset-${jurisdiction.toLowerCase()}.csv`;
};
